import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Field, FormikProvider, useFormik } from 'formik';
import { Button, Select, TextField, Text, Card, Icon, StatusIconProtoProps } from '@nike/eds';
import * as Yup from 'yup';
import { MembershipLearnMore } from './membership-learn-more';
import { GovtCompliance } from './govt-compliance';
import { EmailLanguageGroup, PassPolicy } from '../../../../models/IPassPolicy';
import { useI18nContext } from '../../../../context/language/use-i18n-context';
import { Languages } from '../../../../models/translations';
import { AppConfig } from '../../../../utils/constants';
import { LanguageOption, SlotOption, StoreOption } from '../type';
import { GetPolicyShoppingTimes, registerPass } from '../../../../services/pass-service';
import { Reservations } from '../../../../models/IReservation';
import { dateFormat, getMinDate, getSetDates } from '../../../../utils/date-helper';
import { GuestPassRequest } from '../../../../models/IGuestPass';
import { ts } from '../i18n';
import translations from '../../../shared/i18n';
import { useLanguageContext } from '../../../../context/language/context';
import { useUserContext } from '../../../../context/user';
import { HttpStatusCode } from 'axios';
import { GuestPass, PassRequest } from '../../../../models/IPassRequest';
import { GetPassLocations } from '../../../../services/pass-location.service';
import { sortPassLocations } from '../../../../utils/helper';
import LegalTextBanner from 'components/shared/legaltext-banner/legaltext-banner';

type TsKeys = keyof typeof translations;
export interface Props {
  passPolicy: PassPolicy;
  guestPassToEdit: GuestPassRequest;
  onDismiss: () => void;
  setSnackbar: Dispatch<
    SetStateAction<{
      showSnack: boolean;
      message: string;
      snackStatus: StatusIconProtoProps['status'];
    }>
  >;
}
const guestPassDefault: GuestPass = {
  passId: null,
  sponsorId: 0,
  passTypeId: 10,
  firstName: '',
  lastName: '',
  emailAddress: '',
  shoppingDateStart: new Date(),
  shoppingDateEnd: new Date(),
  passLocationId: 0,
  passPolicyId: 0,
  relationshipId: null,
  weChatId: null,
  timeSlotId: null,
  govtComplianceChecked: false,
  emailLanguageGroupId: null,
  guestEmailLanguage: '',
};

export const GuestForm: FC<Props> = ({ passPolicy, guestPassToEdit, onDismiss, setSnackbar }) => {
  const { i18nString } = useI18nContext();
  const userInfo = useUserContext();
  const [language] = useLanguageContext();
  const [dateRange, setDateRangeValue] = useState<string>('');
  const [shoppingTimes, setShoppingTimes] = useState<SlotOption[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const isSingleDay =
    dateFormat(passPolicy.shoppingStartDate) === dateFormat(passPolicy.shoppingEndDate);
  const [emailLanguages, setEmailLanguages] = useState<LanguageOption[]>([]);
  const [passLocations, setPassLocations] = useState<StoreOption[]>([]);
  const [govtComplianceChecked, setGovtComplianceChecked] = useState<boolean>(false);

  const handleSubmit = async () => {
    setSaving(true);
    const guestPass: PassRequest = {
      approver: userInfo.passplayUser?.selfApprovalId ?? '',
      guestPasses: [
        {
          ...guestPassDefault,
          passId: guestPassToEdit.passId ?? null,
          firstName: formik.values.firstName,
          sponsorId: userInfo.passplayUser?.sponsorId ?? 0,
          lastName: formik.values.lastName,
          emailAddress: formik.values.emailAddress,
          passPolicyId: passPolicy?.passPolicyId,
          shoppingDateStart: new Date(formik.values.shoppingDateStart),
          shoppingDateEnd: new Date(formik.values.shoppingDateEnd),
          passLocationId: formik.values.passLocationId,
          emailLanguageGroupId: passPolicy.emailLanguageGroupId,
          timeSlotId: formik.values.timeSlotId !== 0 ? formik.values.timeSlotId : null,
          govtComplianceChecked: govtComplianceChecked,
          guestEmailLanguage: formik.values.guestEmailLanguage,
        } as GuestPass,
      ],
    };
    try {
      const response = await registerPass(userInfo.passplayUser?.sponsorId, guestPass);
      if (response.status === HttpStatusCode.Ok) {
        let message = ts.TOASTR_PASS_ADDED;
        if (guestPassToEdit.passId ?? 0 > 0) {
          message = ts.TOASTR_CHANGES_SAVED;
        }
        setSnackbar({
          showSnack: true,
          message: i18nString(message).replace(/\{{\w+\}}/g, 'GUEST'),
          snackStatus: 'success' as StatusIconProtoProps['status'],
        });
      } else {
        setSnackbar({
          showSnack: true,
          message: i18nString(ts.TOASTR_PASS_ERROR),
          snackStatus: 'error' as StatusIconProtoProps['status'],
        });
      }
      formik.resetForm();
      onDismiss();
    } catch (e) {
      setSaving(false);
      setSnackbar({
        showSnack: true,
        message: i18nString(ts.TOASTR_PASS_ERROR),
        snackStatus: 'error' as StatusIconProtoProps['status'],
      });
      onDismiss();
    }
    setSaving(false);
  };

  const validationSchema = Yup.object().shape({
    firstName: passPolicy.selfInviteOnly
      ? Yup.string().notRequired()
      : Yup.string()
          .required(' ')
          .matches(
            RegExp(AppConfig.regexPatterns.regexPersonNames),
            AppConfig.validationMessages.namesErrorMessage,
          ),
    lastName: passPolicy.selfInviteOnly
      ? Yup.string().notRequired()
      : Yup.string()
          .required(' ')
          .matches(
            RegExp(AppConfig.regexPatterns.regexPersonNames),
            AppConfig.validationMessages.namesErrorMessage,
          ),
    emailAddress:
      passPolicy.selfInviteOnly || passPolicy.emailFlag !== AppConfig.emailFlags.required
        ? Yup.string().notRequired()
        : Yup.string()
            .required(' ')
            .matches(
              RegExp(AppConfig.regexPatterns.regexEmail),
              i18nString(ts.PASS_MODAL_EMAIL_ERROR),
            ),
    passLocationId: Yup.number().required(),
    guestEmailLanguage:
      passPolicy.emailLanguageGroupId !== null
        ? Yup.string().required()
        : Yup.string().notRequired().nullable(),
    shoppingDateStart: isSingleDay
      ? Yup.string().notRequired()
      : Yup.string()
          .required(' ')
          .test('starDate-check', async function (value) {
            if (!value) return false;
            try {
              const yesterdayDate = new Date();
              yesterdayDate.setDate(yesterdayDate.getDate() - 1);
              yesterdayDate.setHours(0, 0, 0, 0);
              const inputDate = new Date(value);
              inputDate.setHours(0, 0, 0, 0);
              if (inputDate > new Date(passPolicy.shoppingEndDate) || inputDate < yesterdayDate) {
                return false;
              } else {
                return true;
              }
            } catch (error) {
              return this.createError({ message: 'Error validating shoppingDateStart' });
            }
          }),
    timeSlotId: passPolicy.reservationFlag
      ? Yup.number().required()
      : Yup.string().notRequired().nullable(),
  });
  const formik = useFormik({
    validateOnMount: true,
    initialValues: guestPassToEdit,
    enableReinitialize: true,
    onSubmit: () => {
      formik.resetForm();
    },
    validationSchema: validationSchema,
  });
  useEffect(() => {
    if (passPolicy) {
      GetPassLocations(passPolicy?.passLocationGroupId).then(response => {
        // const selectStore: StoreOption= { value: 0, label: i18nString(ts.DDL_STORE_SELECT),shoppingDays: 0 };
        const storeOptions = sortPassLocations(response.data);
        setPassLocations(storeOptions);
        if (storeOptions.length === 1) {
          formik.setFieldValue('passLocationId', storeOptions[0].value);
        }
        validateDate();
      });
      if (passPolicy.emailLanguageGroupId !== null) {
        const selectLanguage: LanguageOption = {
          value: '',
          label: i18nString(ts.HEADER_BAR_LANGUAGE),
        };
        const languages: LanguageOption[] = passPolicy?.emailLanguageGroups?.map(
          (lang: EmailLanguageGroup) => ({ value: lang.language, label: lang.languageName }),
        );
        languages.unshift(selectLanguage);
        setEmailLanguages(languages);
      }
    }
  }, [passPolicy]);

  useEffect(() => {
    if (
      passPolicy?.reservationFlag &&
      formik.values.shoppingDateStart &&
      formik.values.passLocationId !== 0 &&
      passPolicy.passPolicyId
    ) {
      GetPolicyShoppingTimes(
        passPolicy.passPolicyId,
        formik.values.passLocationId ?? 0,
        formik.values.shoppingDateStart,
      ).then(response => {
        if (response && response.data.dates.length > 0) {
          const selectSlot: SlotOption = {
            label: i18nString(ts.DDL_SHOPPING_TIME_SELECT),
            isDisabled: true,
            value: 0,
          };
          const shoppingTimes = response.data.dates[0].reservations.map(
            (slot: Reservations): SlotOption => ({
              value: slot.id,
              label: slot.description,
              isDisabled: slot.available < 1 ? true : false,
            }),
          );
          setShoppingTimes([selectSlot, ...shoppingTimes]);
        } else
          setShoppingTimes([
            { label: i18nString(ts.DDL_NO_SHOPPING_TIME), isDisabled: true, value: 0 },
          ]);
      });
    }
  }, [passPolicy, formik.values.shoppingDateStart, formik.values.passLocationId]);

  useEffect(() => {
    if (formik.values.shoppingDateStart) onDateChange(formik.values.shoppingDateStart);
  }, [formik.values.passLocationId]);

  const handleDateChange = (event: any) => {
    const value = event.target.value;
    if (event.target.value.length < 10) {
      formik.setFieldValue('shoppingDateStart', value);
      return;
    }
    onDateChange(event.target.value);
    formik.setFieldValue('shoppingDateStart', value);
  };

  const onDateChange = (startDate: string) => {
    const shoppingDays =
      passLocations.find(x => x.value === formik.values.passLocationId)?.shoppingDays ?? null;
    const dateObj = getSetDates(
      startDate,
      formik.values.shoppingDateEnd,
      passPolicy,
      shoppingDays,
      userInfo?.passplayUser?.userCountry || '',
    );
    if (dateObj.isValid) {
      formik.setFieldValue('shoppingDateEnd', dateObj.endDate);
    } else {
      setDate();
    }
    if (dateObj.dateAlertText !== '') {
      setDateAlert(dateObj.dateAlertText, dateObj.shoppingDays);
    } else setDateRangeValue('');
  };

  const validateDate = () => {
    if (guestPassToEdit && guestPassToEdit.shoppingDateStart !== '') {
      const shoppingDays =
        passLocations.find(x => x.value === formik.values.passLocationId)?.shoppingDays ?? 0;
      const dateObj = getSetDates(
        guestPassToEdit.shoppingDateStart,
        guestPassToEdit.shoppingDateEnd,
        passPolicy,
        shoppingDays,
        userInfo?.passplayUser?.userCountry || '',
      );
      if (!dateObj.isValid) {
        setDate();
      }
      if (dateObj.dateAlertText !== '') setDateAlert(dateObj.dateAlertText, dateObj.shoppingDays);
      else setDateRangeValue('');
    }
  };
  const setDate = () => {
    formik.setFieldValue('shoppingDateEnd', '');
  };
  const setDateAlert = (dateAlertText: string, shoppingDays: number) => {
    const alertText = i18nString(translations[dateAlertText as TsKeys]);
    setDateRangeValue(alertText.replace('{{number}}', shoppingDays.toString()));
  };

  const handleGovtComplianceChange = (checked: boolean) => {
    setGovtComplianceChecked(checked);
  };

  const isFormEmpty = (values: GuestPassRequest): boolean => {
    return (
      values.firstName === '' ||
      values.lastName === '' ||
      (passPolicy.emailFlag === 2 && values.emailAddress === '') ||
      values.passLocationId === 0 ||
      values.shoppingDateStart === '' ||
      (passPolicy.emailLanguageGroupId !== null && values.guestEmailLanguage === '') ||
      (passPolicy.reservationFlag === true && values.timeSlotId === 0) ||
      (passPolicy.govtComplianceFlag === 1 && !govtComplianceChecked)
    );
  };
  useEffect(() => {
    formik.resetForm();
    setGovtComplianceChecked(false);
    setDateRangeValue('');
  }, [passPolicy]);

  return (
    <section className="eds-spacing--mt-16 form_wrapper">
      <FormikProvider value={formik}>
        <Card className="section-border form_wrapper">
          <div className="guest__form-card eds-flex eds-flex--direction-row eds-flex--wrap eds-flex--align-content-flex-start eds-flex--justify-content-space-between">
            <div className="column">
              <Text className="h4_font eds-spacing--mt-12 eds-spacing--mt-4" as={'h4'}>
                {passPolicy?.selfInviteOnly
                  ? i18nString(ts.PASS_MODAL_GUEST_INFO_HEADING_SELF_INVITE)
                  : i18nString(ts.PASS_MODAL_GUEST_INFO_HEADING)}
              </Text>
              {language !== Languages.JA && (
                <TextField
                  id="firstName"
                  label={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                  aria-label={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                  hideLabel={true}
                  placeholder={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                  disabled={passPolicy?.selfInviteOnly}
                  type="text"
                  name="firstName"
                  autoFocus={true}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="guest__form-card eds-spacing--mt-16"
                  value={formik.values.firstName}
                  data-testid="first_name"
                  hasErrors={!!formik.errors.firstName && formik.touched.firstName}
                  errorMessage={formik.touched.firstName ? formik.errors.firstName : undefined}
                />
              )}
              <TextField
                id="lastName"
                label={i18nString(ts.PLACEHOLDER_LAST_NAME)}
                aria-label={i18nString(ts.PLACEHOLDER_LAST_NAME)}
                hideLabel={true}
                placeholder={i18nString(ts.PLACEHOLDER_LAST_NAME)}
                type="text"
                name="lastName"
                disabled={passPolicy?.selfInviteOnly}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.lastName}
                data-testid="last_name"
                className="guest__form-card eds-spacing--mt-16"
                hasErrors={!!formik.errors.lastName && formik.touched.lastName}
                errorMessage={formik.touched.lastName ? formik.errors.lastName : undefined}
              />

              {language === Languages.JA && (
                <TextField
                  id="firstName-jp"
                  label={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                  hideLabel={true}
                  placeholder={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                  type="text"
                  name="firstName"
                  autoFocus={true}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.firstName}
                  disabled={passPolicy?.selfInviteOnly}
                  data-testid="first_name"
                  className="guest__form-card eds-spacing--mt-16"
                  hasErrors={!!formik.errors.firstName && formik.touched.firstName}
                  errorMessage={formik.touched.firstName ? formik.errors.firstName : undefined}
                />
              )}
              <div className="guest__form-card">
                {passPolicy.emailFlag !== AppConfig.emailFlags.none && (
                  <div
                    className={
                      passPolicy.emailLanguageGroupId !== null
                        ? 'f1 email__input'
                        : 'f1 input_width'
                    }
                  >
                    <TextField
                      id="emailAddress"
                      label={i18nString(ts.PLACEHOLDER_EMAIL_ADDRESS)}
                      aria-label={i18nString(ts.PLACEHOLDER_EMAIL_ADDRESS)}
                      hideLabel={true}
                      type="email"
                      name="emailAddress"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.emailAddress}
                      data-testid="email_address"
                      className="eds-spacing--mt-16"
                      hasErrors={formik.touched.emailAddress && !!formik.errors.emailAddress}
                      placeholder={
                        passPolicy.emailFlag == AppConfig.emailFlags.membership ||
                        passPolicy.emailFlag == AppConfig.emailFlags.optin
                          ? i18nString(ts.PLACEHOLDER_GUEST_MEMBERSHIP)
                          : passPolicy?.selfInviteOnly
                            ? i18nString(ts.PLACEHOLDER_MY_EMAIL)
                            : i18nString(ts.PLACEHOLDER_GUEST_EMAIL)
                      }
                      errorMessage={
                        formik.touched.emailAddress ? formik.errors.emailAddress : undefined
                      }
                    />
                  </div>
                )}
                {passPolicy.emailLanguageGroupId !== null && (
                  <div className="f1" style={{ width: '40%' }}>
                    <Field name="guestEmailLanguage">
                      {({ field }: { field: any }) => (
                        <Select
                          {...field}
                          id="emailLanguage"
                          data-testid="email_language"
                          options={emailLanguages}
                          aria-label="emaillanguage"
                          label="emaillanguage"
                          name="guestEmailLanguage"
                          className="eds-spacing--mt-16"
                          value={
                            emailLanguages.find(
                              option => option.value === formik.values.guestEmailLanguage,
                            ) || ''
                          }
                          onChange={(option: any) => {
                            formik.setFieldValue('guestEmailLanguage', option.value);
                          }}
                          onBlur={formik.handleBlur}
                          hideLabel={true}
                          isSearchable={false}
                          hasErrors={
                            formik.touched.guestEmailLanguage && !!formik.errors.guestEmailLanguage
                          }
                          errorMessage={
                            typeof formik.errors.guestEmailLanguage === 'string'
                              ? formik.errors.guestEmailLanguage
                              : undefined
                          }
                        />
                      )}
                    </Field>
                  </div>
                )}
                <div className="cb"></div>
                {passPolicy.emailFlag !== AppConfig.emailFlags.none &&
                  passPolicy.emailFlag !== AppConfig.emailFlags.membership &&
                  passPolicy.emailFlag !== AppConfig.emailFlags.optin &&
                  passPolicy.notifyGuestFlag && (
                    <p id="conEmailpText" className="text--help-text">
                      {i18nString(ts.PASS_MODAL_EMAIL_CONFIRMATION)}
                    </p>
                  )}
                {(passPolicy.emailFlag === AppConfig.emailFlags.membership ||
                  passPolicy.emailFlag === AppConfig.emailFlags.optin) && <MembershipLearnMore />}
                {!!formik.errors.emailAddress && formik.touched.emailAddress && (
                  <span className="error__text">
                    {/* {i18nString(ts.PASS_MODAL_EMAIL_ERROR)} */}
                    <Icon
                      className="error__icon glyphicon"
                      name="AlertCircleFilled"
                      data-testid="AlertCircle"
                      style={{ color: '#EE446A', top: '20px' }}
                    />
                  </span>
                )}
              </div>
            </div>
            <div className="column">
              <Text className="h4_font eds-spacing--mt-12 eds-spacing--mx-4" as={'h4'}>
                {i18nString(ts.PASS_MODAL_STORE_HEADING)}
              </Text>
              <Field name="passLocationId">
                {({ field }: { field: any }) => (
                  <Select
                    {...field}
                    id="passLocationId"
                    data-testid="pass_location"
                    options={passLocations}
                    label="store"
                    aria-label="store"
                    name="passLocationId"
                    className="guest__form-card eds-spacing--mt-16"
                    value={
                      passLocations.find(option => option.value === formik.values.passLocationId) ||
                      ''
                    }
                    onChange={(option: any) => {
                      formik.setFieldValue('passLocationId', option.value);
                    }}
                    hideLabel={true}
                    onBlur={formik.handleBlur}
                    isSearchable={false}
                    hasErrors={formik.touched.passLocationId && !!formik.errors.passLocationId}
                    errorMessage={
                      typeof formik.errors.passLocationId === 'string'
                        ? formik.errors.passLocationId
                        : undefined
                    }
                  />
                )}
              </Field>
              <Text className="h4_font eds-spacing--mt-12 eds-spacing--mx-4" as={'h4'}>
                {i18nString(translations.PASS_MODAL_DATE_RANGE_HEADING)}
              </Text>
              <div className="eds-flex eds-flex--justify-content-space-between eds-flex--direction-row eds-flex--noWrap eds-spacing--mt-16 row">
                <div className="input__container" style={{ width: 'calc(50% - 4px)' }}>
                  <TextField
                    label="shoppingDateStart"
                    hideLabel={true}
                    disabled={isSingleDay}
                    type="date"
                    name="shoppingDateStart"
                    data-testid="start_date"
                    value={formik.values.shoppingDateStart}
                    onChange={e => {
                      handleDateChange(e);
                    }}
                    onBlur={e => {
                      formik.handleBlur(e);
                    }}
                    min={getMinDate()}
                    id="shoppingDateStart"
                    hasErrors={
                      !!formik.errors.shoppingDateStart && formik.touched.shoppingDateStart
                    }
                    errorMessage={
                      formik.touched.shoppingDateStart ? formik.errors.shoppingDateStart : undefined
                    }
                  />
                </div>
                {!passPolicy.reservationFlag && (
                  <div className="input__container" style={{ width: 'calc(50% - 4px)' }}>
                    <TextField
                      label="shoppingDateEnd"
                      hideLabel={true}
                      disabled={true}
                      aria-label="shoppingDateEnd"
                      name="shoppingDateEnd"
                      type="date"
                      value={formik.values.shoppingDateEnd}
                      id="shoppingDateEnd"
                      errorMessage={formik.values.shoppingDateEnd}
                    />
                  </div>
                )}
              </div>
              {passPolicy.shoppingStartDate && (
                <p id="dateRangeText" className="text--help-text">
                  {dateRange}
                </p>
              )}
              {passPolicy.reservationFlag && (
                <Text className="txt_uppercase eds-spacing--mt-24 eds-spacing--mx-4" as={'h4'}>
                  Reserve
                </Text>
              )}
              {passPolicy.reservationFlag && (
                <Field name="timeSlotId">
                  {({ field }: { field: any }) => (
                    <Select
                      {...field}
                      id="shoppingTimeMenu"
                      data-testid="shopping_time"
                      options={shoppingTimes}
                      aria-label="shoppingtime"
                      label="shoppingtime"
                      name="shoppingTimeMenu"
                      value={
                        shoppingTimes.find(option => option.value === formik.values.timeSlotId) ||
                        ''
                      }
                      onChange={(option: any) => {
                        formik.setFieldValue('timeSlotId', option.value);
                      }}
                      hideLabel={true}
                      isSearchable={false}
                      className="guest__form-card eds-spacing--mt-16"
                      hasErrors={formik.touched.timeSlotId && !!formik.errors.timeSlotId}
                      errorMessage={
                        typeof formik.errors.timeSlotId === 'string'
                          ? formik.errors.timeSlotId
                          : undefined
                      }
                    />
                  )}
                </Field>
              )}
            </div>
          </div>
          <div>
            {passPolicy.govtComplianceFlag > 0 && (
              <GovtCompliance
                onComplianceChange={handleGovtComplianceChange}
                policyLocationId={passPolicy.policyLocationId}
                isChecked={govtComplianceChecked}
              />
            )}
            <LegalTextBanner className={'eds-spacing--mt-8'} passPolicy={passPolicy} />
          </div>
        </Card>
      </FormikProvider>
      {((passPolicy.passesUsed || 0) < (passPolicy.passesAllowed || 0) ||
        passPolicy.passesAllowed === 0 ||
        (guestPassToEdit.passId && guestPassToEdit.passId > 0)) && (
        <div className="eds-flex eds-flex--justify-content-center eds-spacing--mt-48">
          {!saving && (
            <Button
              type="submit"
              disabled={!formik.isValid || isFormEmpty(formik.values)}
              className="txt_uppercase"
              aria-label="saveGuestPassButton"
              variant="primary"
              data-testid="register"
              onClick={handleSubmit}
            >
              {guestPassToEdit.passId && guestPassToEdit.passId > 0
                ? i18nString(ts.PASS_MODAL_UPDATE)
                : i18nString(ts.PASS_MODAL_REGISTER)}
            </Button>
          )}
          {saving && <span>{i18nString(ts.SAVING_TEXT)}</span>}
        </div>
      )}
    </section>
  );
};
